import React from 'react';
import { askOrbitAction } from '../../components/AskOrbit';
import { aaiApi } from '../../services/aaiApi';
import { useParticipant } from '../../hooks/useParticipant';
import { useAsync } from '../aai/useAsync';
import type { AaiContext, WhatsAllowedData } from '../../types/aai';
import { ErrorNote, Loading, PageBody, PageHead } from '../../components/aa';
import type { ActivityLinkResolver } from '../partner/ActivityView';
import { ClientContextBody } from '../partner/ClientContextPage';

/*
 * Company Context, for the employee. The same body the Owner, the Department
 * Head and the Champion read, rendered for a viewer whose access is their own
 * work plus assigned Projects. Orbit cycles are not readable by this role, so
 * the Overview reports that findings arrive once the program shares them
 * rather than asking for a read the server would refuse.
 */

const employeeLinks: ActivityLinkResolver = (event) => {
  if (event.entity_type === 'request' && event.entity_id) return { label: 'Open project', to: `/my-projects?open=${event.entity_id}` };
  if (event.entity_type === 'skill') return { label: 'Open Quick Wins', to: '/my-projects?tab=quick_wins' };
  if (event.entity_type === 'source') return { label: 'Open company tools', to: '/company-context?tab=sources' };
  return null;
};

// This role holds no Orbit collection read; the body renders its no-cycle state.
const NO_CYCLE = { view: null, loading: false, error: null };

const CompanyContextPage: React.FC = () => {
  const { context: me } = useParticipant();
  const clientId = localStorage.getItem('client_id') || '';
  const clientName = localStorage.getItem('client_name') || 'Your company';

  const context = useAsync<AaiContext>(() => aaiApi.getContext(), 'Could not load your context.');
  const allowed = useAsync<WhatsAllowedData>(() => aaiApi.getWhatsAllowed(), 'Could not load company context.');

  if (allowed.loading) return <PageBody><Loading label="Loading company context" /></PageBody>;
  if (allowed.error || !allowed.data) {
    return <PageBody><ErrorNote message={allowed.error || 'Something went wrong.'} /></PageBody>;
  }

  const companyName = context.data?.company_name || clientName;
  const department = me?.department || context.data?.department || 'No department recorded';

  return (
    <PageBody>
      <PageHead
        align="center"
        title="Company Context"
        sub="What the company is trying to achieve, how work happens, who owns decisions, and what is ready to improve."
        actions={askOrbitAction()}
      />
      <ClientContextBody
        viewer="employee"
        clientId={clientId}
        clientName={companyName}
        allowed={allowed.data}
        cycle={NO_CYCLE}
        links={employeeLinks}
      />
    </PageBody>
  );
};

export default CompanyContextPage;
